import React, { useState, useEffect } from 'react';
import { FaUser, FaEnvelope, FaPhone, FaLock, FaUserTag, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import Swal from 'sweetalert2';
import PropTypes from 'prop-types';
import { userAPI } from '../../../utils/api';

function UserCreate({ user = null, onClose, showSpinner = false }) {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    status: 'ACTIVE',
    password: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditMode = !!user;

  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username || '',
        email: user.email || '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phone: user.phone || '',
        status: user.status || 'ACTIVE',
        password: '',
      });
    }
  }, [user]);

  const validateField = (name, value) => {
    switch (name) {
      case 'username':
        if (!value.trim()) return 'Username is required';
        if (value.trim().length < 3) return 'Username must be at least 3 characters';
        break;
      case 'email':
        if (!value.trim()) return 'Email is required';
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) return 'Invalid email address';
        break;
      case 'firstName':
        break;
      case 'lastName':
        break;
      case 'phone':
        break;
      case 'status':
        if (!value) return 'Status is required';
        break;
      case 'password':
        if (!isEditMode && !value) return 'Password is required';
        if (value && value.length < 6) return 'Password must be at least 6 characters';
        break;
      default:
        break;
    }
    return '';
  };

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
  };

  const validateForm = () => {
    const newErrors = {};
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validateForm()) {
      Swal.fire({
        title: 'Validation Error',
        text: 'Please fix the errors in the form.',
        icon: 'error',
        confirmButtonColor: '#f44336',
      });
      return;
    }
    setIsSubmitting(true);
    try {
      let payload;
      if (isEditMode) {
        payload = {
          username: formData.username,
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          status: formData.status,
          userType: user?.userType || 'REGULAR',
        };
      } else {
        payload = {
          username: formData.username,
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          status: formData.status,
          userType: 'REGULAR',
        };
      }
      let res;
      if (isEditMode) {
        res = await userAPI.updateUser(user.id, payload);
      } else {
        res = await userAPI.createUser(payload);
      }
      Swal.fire({
        title: 'Success',
        text: isEditMode ? 'User updated successfully!' : 'User created successfully!',
        icon: 'success',
        confirmButtonColor: '#4CAF50',
      });
      setFormData({ username: '', email: '', role: '', password: '' });
      setErrors({});
      if (onClose) onClose(res?.data);
    } catch (error) {
      let msg = error.message;
      if (error.response && error.response.data && error.response.data.message) {
        msg = error.response.data.message;
      }
      Swal.fire({
        title: 'Error',
        text: msg || 'Failed to save user. Please try again.',
        icon: 'error',
        confirmButtonColor: '#f44336',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (onClose) {
      onClose();
    }
  };

  return (
    <div className="eduss-user-create-modal">
      <div className="eduss-user-create-header">
        <div className="eduss-user-create-header-content">
          <div className="eduss-user-create-icon">
            <FaUser />
          </div>
          <div className="eduss-user-create-title">
            <h3>{isEditMode ? 'Edit User Account' : 'Create New User Account'}</h3>
            <p>
              {isEditMode
                ? 'Update user information and account settings'
                : 'Add a new user to the system with appropriate permissions'}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="eduss-user-create-form">
        <div className="eduss-user-create-sections">
          {/* Account Information Section */}
          <div className="eduss-user-create-section">
            <div className="eduss-user-create-section-header">
              <FaUserTag className="eduss-user-create-section-icon" />
              <h4>Account Information</h4>
            </div>
            <div className="eduss-user-create-field-grid">
              <div className="eduss-user-create-field">
                <label htmlFor="username">
                  Username <span className="eduss-user-create-required">*</span>
                </label>
                <div className="eduss-user-create-input-wrapper">
                  <FaUser className="eduss-user-create-input-icon" />
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    placeholder="Enter username"
                    className={errors.username ? 'eduss-user-create-input error' : 'eduss-user-create-input'}
                    disabled={isSubmitting || showSpinner}
                  />
                </div>
                {errors.username && (
                  <div className="eduss-user-create-error">
                    <FaTimesCircle className="eduss-user-create-error-icon" />
                    {errors.username}
                  </div>
                )}
              </div>

              <div className="eduss-user-create-field">
                <label htmlFor="email">
                  Email Address <span className="eduss-user-create-required">*</span>
                </label>
                <div className="eduss-user-create-input-wrapper">
                  <FaEnvelope className="eduss-user-create-input-icon" />
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter email address"
                    className={errors.email ? 'eduss-user-create-input error' : 'eduss-user-create-input'}
                    disabled={isSubmitting || showSpinner}
                  />
                </div>
                {errors.email && (
                  <div className="eduss-user-create-error">
                    <FaTimesCircle className="eduss-user-create-error-icon" />
                    {errors.email}
                  </div>
                )}
              </div>

              {!isEditMode && (
                <div className="eduss-user-create-field">
                  <label htmlFor="password">
                    Password <span className="eduss-user-create-required">*</span>
                  </label>
                  <div className="eduss-user-create-input-wrapper">
                    <FaLock className="eduss-user-create-input-icon" />
                    <input
                      type="password"
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Enter password"
                      className={errors.password ? 'eduss-user-create-input error' : 'eduss-user-create-input'}
                      disabled={isSubmitting || showSpinner}
                    />
                  </div>
                  {errors.password && (
                    <div className="eduss-user-create-error">
                      <FaTimesCircle className="eduss-user-create-error-icon" />
                      {errors.password}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Personal Information Section */}
          <div className="eduss-user-create-section">
            <div className="eduss-user-create-section-header">
              <FaUser className="eduss-user-create-section-icon" />
              <h4>Personal Information</h4>
            </div>
            <div className="eduss-user-create-field-grid">
              <div className="eduss-user-create-field">
                <label htmlFor="firstName">First Name</label>
                <div className="eduss-user-create-input-wrapper">
                  <FaUser className="eduss-user-create-input-icon" />
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="Enter first name"
                    className="eduss-user-create-input"
                    disabled={isSubmitting || showSpinner}
                  />
                </div>
              </div>

              <div className="eduss-user-create-field">
                <label htmlFor="lastName">Last Name</label>
                <div className="eduss-user-create-input-wrapper">
                  <FaUser className="eduss-user-create-input-icon" />
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Enter last name"
                    className="eduss-user-create-input"
                    disabled={isSubmitting || showSpinner}
                  />
                </div>
              </div>

              <div className="eduss-user-create-field">
                <label htmlFor="phone">Phone Number</label>
                <div className="eduss-user-create-input-wrapper">
                  <FaPhone className="eduss-user-create-input-icon" />
                  <input
                    type="text"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Enter phone number"
                    className="eduss-user-create-input"
                    disabled={isSubmitting || showSpinner}
                  />
                </div>
              </div>

              <div className="eduss-user-create-field">
                <label htmlFor="status">
                  Account Status <span className="eduss-user-create-required">*</span>
                </label>
                <div className="eduss-user-create-input-wrapper">
                  <FaCheckCircle className="eduss-user-create-input-icon" />
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className={errors.status ? 'eduss-user-create-select error' : 'eduss-user-create-select'}
                    disabled={isSubmitting || showSpinner}
                  >
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                  </select>
                </div>
                {errors.status && (
                  <div className="eduss-user-create-error">
                    <FaTimesCircle className="eduss-user-create-error-icon" />
                    {errors.status}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="eduss-user-create-actions">
          <button
            type="button"
            onClick={handleCancel}
            className="eduss-user-create-btn cancel"
            disabled={isSubmitting || showSpinner}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="eduss-user-create-btn submit"
            disabled={isSubmitting || showSpinner}
          >
            {isSubmitting || showSpinner ? (
              <>
                <div className="eduss-user-create-btn-spinner"></div>
                Saving...
              </>
            ) : (
              <>
                <FaCheckCircle className="eduss-user-create-btn-icon" />
                {isEditMode ? 'Update User' : 'Create User'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

UserCreate.propTypes = {
  user: PropTypes.object,
  onClose: PropTypes.func,
  showSpinner: PropTypes.bool,
};

export default UserCreate;